import { httpService } from "../httpService.service";

// Backend contract — Red-flag PATTERN detection & thresholds (base /api/RedFlag).
// A "pattern" is a cluster of red flags of the same issue type that crosses a
// configured threshold within a rolling window (e.g. 5 access-denied flags in
// cluster X within 14 days). The detector also runs daily server-side; these
// endpoints let the PM view current patterns and tune the thresholds.

// IssueTypeEnum (finalized backend contract) — integer only, no server-provided
// names on every response, so we keep a label map for the threshold editor and
// the flag create/edit forms. Pattern responses also carry issueTypeLabel.
// NOTE: 0 = Unspecified is NOT selectable — the pattern detector ignores it, so
// a flag left at 0 can never contribute to (or raise) a pattern alert.
export const IssueType = {
  0: "Unspecified",
  1: "Supply / stockout",
  2: "Staff absence",
  3: "Data quality",
  4: "Beneficiary complaint",
  5: "Infrastructure",
  6: "Access constraint",
  7: "Safeguarding",
  8: "Financial irregularity",
  9: "Service delay",
  99: "Other",
};

// Selectable issue types for the flag create/edit forms — everything except
// 0 (Unspecified), in the backend's declared order (99/Other last).
export const IssueTypeOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 99].map((id) => ({
  id,
  label: IssueType[id],
}));

// GET /api/RedFlag/patterns?ProjectId&IssueType&ClusterId&ImplementingPartnerId
// → [{ issueType, issueTypeLabel, clusterId, implementingPartnerId,
//      implementingPartnerName, projectId, region, count, windowDays,
//      threshold, summary, flagIds:[] }].
// Grouping is by { issueType, clusterId, implementingPartnerId }, so the same
// issue type can raise separate patterns for different IPs in the same cluster.
// implementingPartnerId / implementingPartnerName are null when the flags in a
// group aren't attributable to a single IP.
export const getRedFlagPatterns = (
  { projectId, issueType, clusterId, implementingPartnerId } = {},
  callback,
  errCallback
) => {
  const params = new URLSearchParams();
  if (projectId) params.append("ProjectId", projectId);
  if (issueType !== undefined && issueType !== null && issueType !== "")
    params.append("IssueType", issueType);
  if (clusterId) params.append("ClusterId", clusterId);
  if (implementingPartnerId)
    params.append("ImplementingPartnerId", implementingPartnerId);
  return httpService.get(
    `/api/RedFlag/patterns?${params.toString()}`,
    callback,
    errCallback
  );
};

// GET /api/RedFlag/patternThresholds →
// [{ id, issueType, issueTypeLabel, minCount, windowDays }].
export const getPatternThresholds = (callback, errCallback) =>
  httpService.get(`/api/RedFlag/patternThresholds`, callback, errCallback);

// PUT /api/RedFlag/patternThresholds — upsert one issue type's threshold.
// Body { issueType, minCount, windowDays }.
export const upsertPatternThreshold = (
  { issueType, minCount, windowDays } = {},
  callback,
  errCallback
) =>
  httpService.put(
    `/api/RedFlag/patternThresholds`,
    { issueType, minCount, windowDays },
    callback,
    errCallback
  );
